import React, { useEffect, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';

interface InlineRichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
  className?: string;
}

// Preset colors for quick selection
const PRESET_COLORS = [
  { name: 'Red', value: '#DC2626' },
  { name: 'Orange', value: '#EA580C' },
  { name: 'Yellow', value: '#F59E0B' },
  { name: 'Green', value: '#16A34A' },
  { name: 'Blue', value: '#2563EB' },
  { name: 'Purple', value: '#9333EA' },
  { name: 'Pink', value: '#DB2777' },
  { name: 'Gray', value: '#6B7280' },
  { name: 'Black', value: '#000000' },
];

// Compact Menu Button
const MenuBtn: React.FC<{
  onClick: () => void;
  isActive?: boolean;
  title: string;
  children: React.ReactNode;
}> = ({ onClick, isActive, title, children }) => (
  <button
    type="button"
    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClick(); }}
    title={title}
    className={`p-1.5 rounded transition-all ${
      isActive ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
    }`}
  >
    {children}
  </button>
);

// Compact Color Picker
const ColorPicker: React.FC<{
  currentColor: string;
  onColorChange: (color: string) => void;
  onClose: () => void;
}> = ({ currentColor, onColorChange, onClose }) => {
  const [customColor, setCustomColor] = useState(currentColor || '#000000');

  return (
    <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl p-2 z-50 min-w-[180px]">
      <div className="text-xs font-medium text-gray-600 mb-1.5">Text Color</div>
      <div className="grid grid-cols-5 gap-1 mb-2">
        {PRESET_COLORS.map((color) => (
          <button
            key={color.value}
            type="button"
            onClick={() => { onColorChange(color.value); onClose(); }}
            title={color.name}
            className={`w-6 h-6 rounded border transition-all hover:scale-110 ${
              currentColor === color.value ? 'border-blue-500 ring-1 ring-blue-200' : 'border-gray-200'
            }`}
            style={{ backgroundColor: color.value }}
          />
        ))}
      </div>
      <div className="flex items-center gap-1 pt-1.5 border-t border-gray-100">
        <input
          type="color"
          value={customColor}
          onChange={(e) => setCustomColor(e.target.value)}
          className="w-6 h-6 rounded cursor-pointer border border-gray-200"
        />
        <input
          type="text"
          value={customColor}
          onChange={(e) => setCustomColor(e.target.value)}
          className="flex-1 px-1.5 py-0.5 text-xs border border-gray-200 rounded w-16"
        />
        <button
          type="button"
          onClick={() => { onColorChange(customColor); onClose(); }}
          className="px-1.5 py-0.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          OK
        </button>
      </div>
      <button
        type="button"
        onClick={() => { onColorChange(''); onClose(); }}
        className="w-full mt-1.5 px-1.5 py-0.5 text-xs text-gray-500 border border-gray-200 rounded hover:bg-gray-50"
      >
        Remove Color
      </button>
    </div>
  );
};

const InlineRichTextEditor: React.FC<InlineRichTextEditorProps> = ({
  content,
  onChange,
  placeholder = 'Enter text...',
  className = '',
}) => {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
      }),
      Placeholder.configure({ placeholder }),
      Underline,
      TextStyle,
      Color,
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    onFocus: () => setIsFocused(true),
    onBlur: () => setIsFocused(false),
  });

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content, { emitUpdate: false });
    }
  }, [content, editor]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.inline-color-picker')) {
        setShowColorPicker(false);
      }
    };
    if (showColorPicker) {
      document.addEventListener('click', handleClickOutside);
    }
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showColorPicker]);

  if (!editor) return null;

  const getCurrentColor = () => editor.getAttributes('textStyle').color || '';
  const setTextColor = (color: string) => {
    if (color) {
      editor.chain().focus().setColor(color).run();
    } else {
      editor.chain().focus().unsetColor().run();
    }
  };

  return (
    <div className={`border border-gray-200 rounded-lg overflow-hidden bg-white ${isFocused ? 'ring-2 ring-blue-500 border-blue-500' : ''} ${className}`}>
      {/* Compact Toolbar */}
      <div className="flex items-center gap-0.5 px-2 py-1 border-b border-gray-100 bg-gray-50">
        <MenuBtn
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          title="Bold"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 4h8a4 4 0 014 4 4 4 0 01-4 4H6z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 12h9a4 4 0 014 4 4 4 0 01-4 4H6z" />
          </svg>
        </MenuBtn>
        <MenuBtn
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          title="Italic"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M10 4h4m-2 0v16m-4 0h8" transform="skewX(-10)" />
          </svg>
        </MenuBtn>
        <MenuBtn
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
          title="Underline"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M7 4v7a5 5 0 0010 0V4M5 20h14" />
          </svg>
        </MenuBtn>
        
        <div className="w-px h-4 bg-gray-200 mx-1" />
        
        {/* Color Picker */}
        <div className="relative inline-color-picker">
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowColorPicker(!showColorPicker); }}
            title="Text Color"
            className={`p-1.5 rounded transition-all flex items-center gap-0.5 ${
              showColorPicker ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
            }`}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10M12 3v14M5 10l7-7 7 7" />
            </svg>
            <div className="w-3 h-1 rounded-sm" style={{ backgroundColor: getCurrentColor() || '#000000' }} />
          </button>
          {showColorPicker && (
            <ColorPicker
              currentColor={getCurrentColor()}
              onColorChange={setTextColor}
              onClose={() => setShowColorPicker(false)}
            />
          )}
        </div>

        <span className="ml-auto text-xs text-blue-500">✨</span>
      </div>

      {/* Editor Content */}
      <EditorContent
        editor={editor}
        className="px-3 py-2 text-sm focus:outline-none [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[24px] [&_.ProseMirror_p]:m-0"
      />
    </div>
  );
};

export default InlineRichTextEditor;
